import React, { useEffect } from 'react';
import { Form } from 'antd';
import { Modal } from 'ui';
import { Input, Select, Switch, TextArea } from 'components/Form';
import { useT } from 'hooks/Locale/useT';
import useQueryApiClient from 'utils/useApiClient';
import { config } from 'config/config';
import { UserValidationRule, UserValidationRuleType } from '../../types';

// The sentinel used everywhere else in this codebase (see e.g. LanguageForm) to tell a fresh
// record apart from one being edited, without a separate "mode" prop.
export type ValidationRuleModalItem = UserValidationRule | { id: 'create' } | null;

interface ValidationRuleFormModalProps {
    item: ValidationRuleModalItem;
    // POST target for create; PUT target is `${baseUrl}/${id}` and is only ever reached when the
    // item being edited already has a numeric id (RESERVED / NON_RESERVABLE). The customer- and
    // organization-scoped routes have no PUT, so those callers only ever pass {id: 'create'}.
    baseUrl: string;
    fixedRuleType?: UserValidationRuleType;
    showOwners?: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

interface ValidationRuleFormValues {
    ruleType?: UserValidationRuleType;
    value: string;
    description?: string | null;
    isActive: boolean;
    customerOwnerIds?: string[];
    organizationOwnerIds?: string[];
}

const formItemLayout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 17 },
};

const ValidationRuleFormModal: React.FC<ValidationRuleFormModalProps> = ({
    item,
    baseUrl,
    fixedRuleType,
    showOwners = false,
    onCancel,
    onSuccess,
}) => {
    const t = useT();
    const [form] = Form.useForm<ValidationRuleFormValues>();

    const isEdit = !!item && item.id !== 'create';

    useEffect(() => {
        if (!item) {
            return;
        }

        if (item.id === 'create') {
            form.resetFields();
            form.setFieldsValue({
                isActive: true,
                ruleType: fixedRuleType,
            });
        } else {
            const rule = item as UserValidationRule;
            form.setFieldsValue({
                ruleType: rule.ruleType,
                value: rule.value,
                description: rule.description,
                isActive: rule.isActive,
                // The owner Select options are keyed by string id (see components/Form/Select) -
                // cast to string here so pre-selected values match, and back to number on submit.
                customerOwnerIds: (rule.owners ?? [])
                    .filter((owner) => owner.ownerType === 'CUSTOMER')
                    .map((owner) => String(owner.ownerId)),
                organizationOwnerIds: (rule.owners ?? [])
                    .filter((owner) => owner.ownerType === 'ORGANIZATION')
                    .map((owner) => String(owner.ownerId)),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    const { appendData: submitRule, isLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: isEdit ? `${baseUrl}/${(item as UserValidationRule)?.id}` : baseUrl,
            method: isEdit ? 'PUT' : 'POST',
        },
        onSuccess: () => {
            form.resetFields();
            onSuccess();
        },
        onError: (error: any) => {
            // The 422 body is a JSON-encoded Laravel validator bag ({field: [messages]}) - map it
            // onto the form so the real reason shows under the field, not just in the global toast.
            if (error?.status !== 422) {
                return;
            }

            try {
                const errors = JSON.parse(error.message);
                form.setFields(
                    Object.entries(errors).map(([name, fieldErrors]) => ({
                        name,
                        errors: fieldErrors as string[],
                    }))
                );
            } catch {
                // Not a field-error body - the global handler already showed a toast for it.
            }
        },
    });

    // Owners are required for RESERVED rules (see the store payload contract) - validate both
    // owner Selects so a missing owner shows as a field error instead of round-tripping to a 422.
    const validateOwnersPresent = async (): Promise<void> => {
        const customerIds: string[] = form.getFieldValue('customerOwnerIds') ?? [];
        const organizationIds: string[] = form.getFieldValue('organizationOwnerIds') ?? [];

        if (customerIds.length === 0 && organizationIds.length === 0) {
            throw new Error(t('validation.field_required'));
        }
    };

    const submitForm = (values: ValidationRuleFormValues) => {
        const owners = showOwners
            ? [
                  ...(values.customerOwnerIds ?? []).map((ownerId) => ({
                      ownerType: 'CUSTOMER',
                      ownerId: Number(ownerId),
                  })),
                  ...(values.organizationOwnerIds ?? []).map((ownerId) => ({
                      ownerType: 'ORGANIZATION',
                      ownerId: Number(ownerId),
                  })),
              ]
            : undefined;

        submitRule({
            ruleType: fixedRuleType ?? values.ruleType,
            value: values.value,
            description: values.description || null,
            isActive: values.isActive ?? true,
            ...(owners ? { owners } : {}),
        });
    };

    return (
        <Modal
            open={!!item}
            isLoading={isLoading}
            onCancel={() => {
                form.resetFields();
                onCancel();
            }}
            title={t(isEdit ? 'general.edit' : 'system.environment.user_validation_add')}
            cancelText={t('general.cancel')}
            okText={t(isEdit ? 'general.save' : 'general.add')}
            onOk={form.submit}
            okButtonProps={{ loading: isLoading }}
        >
            <Form form={form} onFinish={submitForm} {...formItemLayout}>
                <Input
                    name="value"
                    label={t('system.environment.user_validation_value')}
                    customLayout={formItemLayout}
                    validation={{ required: true }}
                />
                <TextArea
                    name="description"
                    label={t('general.description')}
                    customLayout={formItemLayout}
                    maxLength={255}
                />
                <Switch
                    name="isActive"
                    label={t('general.active')}
                    isFormItem
                    customLayout={formItemLayout}
                />
                {showOwners && (
                    <>
                        <Select
                            name="customerOwnerIds"
                            label={t('general.customers')}
                            url={config.api.routes.backend.selectCustomers}
                            dataKey="customers"
                            mode="multiple"
                            allowClear
                            showSearch
                            customLayout={formItemLayout}
                            customRules={[{ validator: validateOwnersPresent }]}
                            onChange={() => form.validateFields(['organizationOwnerIds'])}
                            filterOption={(input: string, option: any) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        />
                        <Select
                            name="organizationOwnerIds"
                            label={t('general.resellers')}
                            url={config.api.routes.backend.selectOrganizations}
                            dataKey="organizations"
                            mode="multiple"
                            allowClear
                            showSearch
                            customLayout={formItemLayout}
                            customRules={[{ validator: validateOwnersPresent }]}
                            onChange={() => form.validateFields(['customerOwnerIds'])}
                            filterOption={(input: string, option: any) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        />
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default ValidationRuleFormModal;
